/**
 * Resolves once this sign-in has passed the backend's device gate.
 *
 * Amplify fires `signedIn` the moment Cognito accepts the password, before the
 * device is trusted. Until POST /auth/devices/check (or the OTP verify) trusts
 * it, every gated request 403s with device_verification_required. The sign-in
 * listeners in security-lifecycle fetched the DEK and the reference data right
 * there, so every new device and every new signup opened with a burst of 403s,
 * and after a new-device OTP nothing re-ran them: the session had no offline
 * copy of the user and clinic (prod, 2026-11-02).
 *
 * GET /auth/me runs every gate and every sign-in path calls it once the device
 * is trusted, so its first success is the signal. Sign-out starts a new
 * generation: waits begun before it never resolve, and a success from a
 * request sent before it cannot release the next user's sign-in work.
 */

let generation = 0;
let release: () => void = () => {};
let trusted: Promise<void> = new Promise(resolve => { release = resolve; });

/** Resolves when the current sign-in has passed the device gate. */
export function whenDeviceTrusted(): Promise<void> {
  return trusted;
}

/** Read before sending a gated request; pass the value to markDeviceTrusted. */
export function deviceTrustGeneration(): number {
  return generation;
}

/** Called on a gated success. A success from an earlier generation is ignored. */
export function markDeviceTrusted(sentInGeneration: number): void {
  if (sentInGeneration === generation) release();
}

/** Called on sign-out: the next sign-in must pass the gate again. */
export function resetDeviceTrust(): void {
  generation += 1;
  trusted = new Promise(resolve => { release = resolve; });
}
